import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, RefreshCw, Clock, ChevronDown, ChevronUp, AlertCircle, History, Zap, Settings, Trash2, Plus, X, ArrowUpDown, Trophy, TrendingUp, List, Database, Stethoscope, CheckCircle, XCircle, Loader } from 'lucide-react';
import styled, { keyframes, css } from 'styled-components';
import apiService from '../../services/apiService';
import { isMarketClosed } from '../../utils/marketCalendar';

const fadeIn = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;
const pulse = keyframes`0%,100%{opacity:1}50%{opacity:.4}`;
const glow = keyframes`0%,100%{box-shadow:0 0 6px #a855f7}50%{box-shadow:0 0 18px #a855f7,0 0 30px #7e22ce}`;

const PageWrap = styled.div`
  min-height:100vh;background:#0a0d12;color:#e2e8f0;font-family:'Inter',sans-serif;padding:24px;
  @media(max-width:480px){padding:12px}
`;
const Header = styled.div`
  display:flex;justify-content:space-between;align-items:center;margin-bottom:28px;flex-wrap:wrap;gap:12px;
`;
const Title = styled.h1`
  font-size:24px;font-weight:700;color:#fff;display:flex;align-items:center;gap:10px;
  .badge{font-size:11px;background:linear-gradient(135deg,#a855f7,#7e22ce);padding:3px 8px;border-radius:12px;font-weight:600;letter-spacing:.5px;}
  @media(max-width:480px){font-size:18px;gap:6px;.badge{font-size:9px;padding:2px 6px}}
`;
const Controls = styled.div`display:flex;gap:10px;align-items:center;flex-wrap:wrap;`;
const Btn = styled.button`
  display:flex;align-items:center;gap:6px;padding:9px 16px;border-radius:8px;border:none;
  cursor:pointer;font-size:13px;font-weight:600;transition:all .2s;
  ${p => p.primary ? css`background:linear-gradient(135deg,#a855f7,#7e22ce);color:#fff;&:hover{opacity:.85}`
    : css`background:rgba(255,255,255,.06);color:#94a3b8;border:1px solid rgba(255,255,255,.1);&:hover{background:rgba(255,255,255,.1);color:#e2e8f0}`}
`;
const MarketClosedBadge = styled.div`
  display:flex;align-items:center;gap:6px;padding:6px 12px;border-radius:6px;
  background:rgba(234,179,8,.08);border:1px solid rgba(234,179,8,.25);color:#eab308;font-size:12px;font-weight:600;
`;
const LiveDot = styled.div`
  width:8px;height:8px;border-radius:50%;background:#a855f7;
  animation:${glow} 2s ease-in-out infinite;
`;
const StatusBar = styled.div`
  background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:10px;
  padding:12px 16px;margin-bottom:20px;display:flex;align-items:center;gap:12px;font-size:13px;color:#64748b;flex-wrap:wrap;
`;
const InfoTag = styled.span`
  font-size:10px;padding:2px 6px;border-radius:4px;
  background:rgba(168,85,247,.12);color:#d8b4fe;border:1px solid rgba(168,85,247,.2);
`;

const SortBar = styled.div`
  display:flex;gap:8px;margin-bottom:14px;align-items:center;flex-wrap:wrap;
  font-size:12px;color:#64748b;
  @media(max-width:480px){gap:4px;font-size:11px}
`;
const SortBtn = styled.button`
  padding:5px 10px;border-radius:6px;font-size:11px;font-weight:600;cursor:pointer;
  border:1px solid ${p => p.active ? 'rgba(168,85,247,.4)' : 'rgba(255,255,255,.1)'};
  background:${p => p.active ? 'rgba(168,85,247,.15)' : 'rgba(255,255,255,.03)'};
  color:${p => p.active ? '#d8b4fe' : '#94a3b8'};
  &:hover{background:rgba(168,85,247,.1);color:#d8b4fe;}
`;

const Card = styled.div`
  background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:14px;
  animation:${fadeIn} .4s ease;overflow:hidden;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const TableHead = styled.div`
  display:grid;grid-template-columns:40px 170px 60px 60px 60px 60px 110px 30px;
  gap:8px;padding:12px 16px;font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:.5px;
  border-bottom:1px solid rgba(255,255,255,.06);font-weight:600;
  min-width: 650px;
`;
const Row = styled.div`
  display:grid;grid-template-columns:40px 170px 60px 60px 60px 60px 110px 30px;
  gap:8px;padding:12px 16px;font-size:13px;align-items:start;
  border-bottom:1px solid rgba(255,255,255,.04);cursor:pointer;transition:background .15s;
  min-width: 650px;
  opacity:${p => p.$unscored ? 0.45 : 1};
  &:hover{background:${p => p.$unscored ? 'rgba(100,116,139,.05)' : 'rgba(168,85,247,.05)'};}
  &:last-child{border-bottom:none;}
`;
const UnscoredDivider = styled.div`
  padding:7px 16px;background:rgba(100,116,139,.06);font-size:11px;color:#475569;
  text-align:center;border-bottom:1px solid rgba(255,255,255,.04);letter-spacing:.3px;
`;
const RankBadge = styled.div`
  font-weight:700;font-size:14px;text-align:center;
  color:${p => p.$rank <= 3 ? '#fbbf24' : p.$rank <= 10 ? '#d8b4fe' : '#64748b'};
`;
const NameCell = styled.div`
  display:flex;flex-direction:column;gap:4px;min-width:0;
  .name{font-weight:600;color:#e2e8f0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .meta{font-size:11px;color:#475569;display:flex;gap:6px;align-items:center;flex-wrap:wrap;}
`;
const ScoreNum = styled.div`
  font-weight:700;text-align:right;
  color:${p => p.$score >= 70 ? '#fbbf24' : p.$score >= 50 ? '#d8b4fe' : p.$score >= 30 ? '#94a3b8' : '#475569'};
  font-size:${p => p.$big ? '15px' : '13px'};
`;
const SubScore = styled.div`
  text-align:right;color:#94a3b8;font-size:12px;
`;
const PassPill = styled.div`
  display:inline-flex;align-items:center;gap:3px;padding:2px 7px;border-radius:5px;font-size:11px;font-weight:600;
  ${p => p.$full ? css`background:rgba(34,197,94,.15);color:#4ade80;border:1px solid rgba(34,197,94,.25)`
    : p.$high ? css`background:rgba(168,85,247,.12);color:#d8b4fe;border:1px solid rgba(168,85,247,.2)`
    : p.$mid ? css`background:rgba(234,179,8,.10);color:#eab308;border:1px solid rgba(234,179,8,.18)`
    : css`background:rgba(255,255,255,.04);color:#64748b;border:1px solid rgba(255,255,255,.08)`}
`;
const VerdictBadge = styled.span`
  font-size:10px;font-weight:700;padding:2px 6px;border-radius:4px;letter-spacing:.3px;
  ${p => p.$v === 'BREAKOUT' ? css`background:rgba(239,68,68,.15);color:#ef4444;border:1px solid rgba(239,68,68,.25)`
    : p.$v === 'NEAR_BREAKOUT' ? css`background:rgba(234,179,8,.12);color:#eab308;border:1px solid rgba(234,179,8,.2)`
    : p.$v === 'FORMING_BASE' ? css`background:rgba(99,102,241,.1);color:#818cf8;border:1px solid rgba(99,102,241,.2)`
    : css`background:rgba(255,255,255,.04);color:#64748b;border:1px solid rgba(255,255,255,.08)`}
`;

const Detail = styled.div`
  background:rgba(0,0,0,.25);padding:18px 24px;border-bottom:1px solid rgba(168,85,247,.15);
  border-top:1px solid rgba(168,85,247,.10);
  animation:${fadeIn} .3s ease;
`;
const ComponentRow = styled.div`
  display:grid;grid-template-columns:120px 1fr 80px 80px;gap:12px;align-items:center;
  padding:6px 0;font-size:12px;
`;
const Bar = styled.div`
  height:8px;background:rgba(255,255,255,.04);border-radius:4px;overflow:hidden;position:relative;
`;
const BarFill = styled.div`
  height:100%;width:${p => p.pct}%;border-radius:4px;
  background:${p => p.pass
    ? 'linear-gradient(90deg,#a855f7,#7e22ce)'
    : 'linear-gradient(90deg,#64748b,#475569)'};
  transition:width .4s ease;
`;
const Empty = styled.div`text-align:center;padding:40px;color:#334155;font-size:14px;`;
const LoadingDot = styled.div`animation:${pulse} 1.4s ease-in-out infinite;text-align:center;padding:40px;color:#a855f7;font-size:28px;`;

const ModalOverlay = styled.div`
  position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.85);
  display:flex;align-items:center;justify-content:center;z-index:1000;backdrop-filter:blur(5px);
`;
const Modal = styled.div`
  background:#0a0d12;border:1px solid rgba(255,255,255,.1);border-radius:16px;
  width:90%;max-width:400px;padding:24px;color:#fff;
`;
const CandidatesModal = styled.div`
  background:#0a0d12;border:1px solid rgba(255,255,255,.1);border-radius:16px;
  width:95%;max-width:780px;max-height:85vh;display:flex;flex-direction:column;color:#fff;
`;
const CandidatesBody = styled.div`overflow-y:auto;flex:1;`;
const CandRow = styled.div`
  display:grid;grid-template-columns:32px 150px 90px 90px 80px 70px 70px;
  gap:8px;padding:10px 16px;font-size:12px;align-items:center;
  border-bottom:1px solid rgba(255,255,255,.04);
  &:hover{background:rgba(168,85,247,.05);}
  &:last-child{border-bottom:none;}
  min-width:600px;
`;
const CandHead = styled(CandRow)`
  font-size:10px;color:#64748b;text-transform:uppercase;letter-spacing:.5px;font-weight:600;
  border-bottom:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.02);
  &:hover{background:rgba(255,255,255,.02);}
`;
const GradeBadge = styled.span`
  font-size:10px;font-weight:700;padding:2px 6px;border-radius:4px;
  ${p => p.$g === 'S' ? 'background:rgba(251,191,36,.15);color:#fbbf24;border:1px solid rgba(251,191,36,.25)'
    : p.$g === 'A' ? 'background:rgba(168,85,247,.12);color:#d8b4fe;border:1px solid rgba(168,85,247,.2)'
    : p.$g === 'B' ? 'background:rgba(148,163,184,.08);color:#94a3b8;border:1px solid rgba(148,163,184,.15)'
    : 'background:rgba(71,85,105,.08);color:#475569;border:1px solid rgba(71,85,105,.15)'}
`;
const RawCandModal = styled(CandidatesModal)`max-width:860px;`;
const RawCandRow = styled.div`
  display:grid;grid-template-columns:32px 150px 90px 90px 80px 1fr;
  gap:8px;padding:10px 16px;font-size:12px;align-items:center;
  border-bottom:1px solid rgba(255,255,255,.04);
  cursor:pointer;&:hover{background:rgba(168,85,247,.04);}
  &:last-child{border-bottom:none;}min-width:580px;
  opacity:${p => p.$filtered ? 0.45 : 1};
`;
const RawCandHead = styled(RawCandRow)`
  font-size:10px;color:#64748b;text-transform:uppercase;letter-spacing:.5px;font-weight:600;
  border-bottom:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.02);
  cursor:default;opacity:1;&:hover{background:rgba(255,255,255,.02);}
`;
const FilterTag = styled.span`
  font-size:10px;padding:2px 7px;border-radius:4px;font-weight:600;
  background:rgba(239,68,68,.1);color:#f87171;border:1px solid rgba(239,68,68,.2);
`;
const PassTag = styled.span`
  font-size:10px;padding:2px 7px;border-radius:4px;font-weight:600;
  background:rgba(34,197,94,.1);color:#4ade80;border:1px solid rgba(34,197,94,.2);
`;
const InlineError = styled.div`
  background:rgba(239,68,68,.06);border:1px solid rgba(239,68,68,.2);border-radius:10px;
  padding:20px 24px;margin-bottom:20px;display:flex;align-items:flex-start;gap:12px;
  animation:${fadeIn} .3s ease;
`;
const RawLoadingBar = styled.div`
  background:rgba(168,85,247,.06);border:1px solid rgba(168,85,247,.2);border-radius:10px;
  padding:14px 20px;margin-bottom:20px;display:flex;align-items:center;gap:10px;
  font-size:13px;color:#a855f7;animation:${fadeIn} .3s ease;
`;
const ApiCheckModal = styled.div`
  background:#0a0d12;border:1px solid rgba(255,255,255,.1);border-radius:16px;
  width:95%;max-width:640px;padding:0;color:#fff;overflow:hidden;
`;
const ApiCheckSection = styled.div`
  padding:20px 24px;border-bottom:1px solid rgba(255,255,255,.06);
  &:last-child{border-bottom:none;}
`;
const ApiStatusRow = styled.div`
  display:flex;align-items:center;gap:10px;margin-bottom:12px;
`;
const ApiStatusBadge = styled.div`
  display:inline-flex;align-items:center;gap:6px;padding:4px 10px;border-radius:6px;font-size:12px;font-weight:700;
  ${p => p.$ok ? css`background:rgba(34,197,94,.12);color:#4ade80;border:1px solid rgba(34,197,94,.25)`
    : p.$error ? css`background:rgba(239,68,68,.1);color:#f87171;border:1px solid rgba(239,68,68,.25)`
    : css`background:rgba(100,116,139,.1);color:#94a3b8;border:1px solid rgba(100,116,139,.2)`}
`;
const FieldTable = styled.div`
  background:rgba(0,0,0,.3);border-radius:8px;padding:12px;font-size:11px;font-family:monospace;
  color:#94a3b8;max-height:200px;overflow-y:auto;line-height:1.7;
`;
const FieldRow = styled.div`
  display:flex;gap:8px;
  .key{color:#d8b4fe;min-width:200px;flex-shrink:0;}
  .val{color:#e2e8f0;word-break:break-all;}
  .zero{color:#475569;}
`;
const spin = keyframes`from{transform:rotate(0deg)}to{transform:rotate(360deg)}`;
const SpinIcon = styled.div`animation:${spin} 1s linear infinite;display:inline-flex;`;

const fmtAmt = v => {
    if (v == null) return '-';
    const abs = Math.abs(v);
    const sign = v < 0 ? '-' : v > 0 ? '+' : '';
    if (abs >= 1_000_000_000_000) return `${sign}${(abs / 1_000_000_000_000).toFixed(2)}조`;
    if (abs >= 100_000_000)       return `${sign}${(abs / 100_000_000).toFixed(1)}억`;
    if (abs >= 10_000_000)        return `${sign}${(abs / 10_000_000).toFixed(1)}천만`;
    return `${sign}${abs.toLocaleString()}원`;
};

const COMPONENT_LABELS = {
    proximity: '저항선근접',
    volatility: '변동성',
    volSurge: '거래량폭발',
    vcp: 'VCP수렴',
    uptrend: '정배열',
    closePosition: '종가위치',
    trendStarter: '추세초입',
    megaVol: '대형거래대금',
    breakoutDay: '돌파일보너스',
    megaCapBreakout: '대형주ATH',
    bulkTransNum: '대량체결건수',
    programBuy: '비차익순매수',
};

const formatComponentValue = (key, c) => {
    if (!c) return '';
    if (key === 'proximity') {
        if (c.value == null || c.threshold == null) return '';
        return `${(c.value * 100).toFixed(1)}% (>${(c.threshold * 100).toFixed(0)}%)`;
    }
    if (key === 'volatility') {
        if (c.value == null || c.threshold == null) return '';
        return `${(c.value * 100).toFixed(1)}% (<${(c.threshold * 100).toFixed(0)}%)`;
    }
    if (key === 'volSurge') {
        if (c.value == null || c.threshold == null) return '';
        return `${c.value.toFixed(2)}x (>${c.threshold.toFixed(1)}x)`;
    }
    if (key === 'vcp') {
        if (c.tailRatio == null) return `dried ${c.driedDays ?? '?'}일`;
        return `tail ${c.tailRatio.toFixed(2)}x · dried ${c.driedDays ?? '?'}일`;
    }
    if (key === 'uptrend') return `MA20:${c.ma20 ?? '-'} 60:${c.ma60 ?? '-'} 120:${c.ma120 ?? '-'}`;
    if (key === 'closePosition') {
        if (c.value == null || c.threshold == null) return '';
        return `${(c.value * 100).toFixed(0)}% (>${(c.threshold * 100).toFixed(0)}%)`;
    }
    if (key === 'trendStarter') return c.active ? '활성' : '미활성';
    if (key === 'megaVol') {
        if (!c.trValue) return '-';
        const t = c.trValue;
        return t >= 1_000_000_000_000
            ? `${(t / 1_000_000_000_000).toFixed(1)}조`
            : `${Math.floor(t / 100_000_000)}억`;
    }
    if (key === 'breakoutDay') {
        if (!c.volSurge) return '-';
        return `${c.volSurge.toFixed(2)}x · 근접 ${c.proximityRatio != null ? (c.proximityRatio * 100).toFixed(1) : '-'}%`;
    }
    if (key === 'megaCapBreakout') return c.isMegaCap ? (c.isNew52WHigh ? '52W신고가' : '대형주') : '-';
    if (key === 'bulkTransNum') return c.rank != null ? `#${c.rank}위` : '순위밖';
    if (key === 'programBuy') return c.netBuyFormatted || (c.netBuyAmt != null ? fmtAmt(c.netBuyAmt) : '-');
    return '';
};

export default function LivermoreV40Page() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [marketClosed] = useState(isMarketClosed(new Date()));
    const [expanded, setExpanded] = useState({});
    const [sortKey, setSortKey] = useState('total');

    const [schedulerConfig, setSchedulerConfig] = useState({
        enabled: true, times: ['09:30', '13:00', '15:00'], weekdaysOnly: true
    });
    const [showSettings, setShowSettings] = useState(false);
    const [showCandidates, setShowCandidates] = useState(false);
    const [rawCandidates, setRawCandidates] = useState(null);
    const [rawLoading, setRawLoading] = useState(false);
    const [rawError, setRawError] = useState(null);
    const [showRaw, setShowRaw] = useState(false);
    const [newTime, setNewTime] = useState('09:00');
    const [showApiCheck, setShowApiCheck] = useState(false);
    const [apiCheckResult, setApiCheckResult] = useState(null);
    const [apiCheckLoading, setApiCheckLoading] = useState(false);

    const isExecutingRef = useRef(false);

    const load = useCallback(async (force = false) => {
        setLoading(true); setError(null);
        try {
            const res = await apiService.getLivermoreV40Leaders(force);
            setData(res);
            setLastUpdate(new Date());
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchScheduler = useCallback(async () => {
        try {
            const res = await apiService.getLivermoreV40Scheduler();
            if (res && res.config) setSchedulerConfig(res.config);
        } catch (e) { console.error('Failed to fetch V40 scheduler', e); }
    }, []);

    useEffect(() => {
        load();
        fetchScheduler();
    }, [load, fetchScheduler]);

    const runAutoSaveProcedure = useCallback(async (config) => {
        if (!config || !config.enabled || !config.times || config.times.length === 0) return;
        const now = new Date();
        const hour = now.getHours();
        const minute = now.getMinutes();

        const kstNow = new Date(now.getTime() + 9 * 60 * 60 * 1000);
        const todayStr = kstNow.toISOString().split('T')[0];

        const currentMins = hour * 60 + minute;
        const storageKey = 'livermore_v40_processed_slots';

        try {
            for (const timeStr of config.times) {
                const [sh, sm] = timeStr.split(':').map(Number);
                const slotMins = sh * 60 + sm;

                if (currentMins >= slotMins && currentMins < slotMins + 30) {
                    let processedData = { date: '', slots: {} };
                    try {
                        const stored = localStorage.getItem(storageKey);
                        if (stored) processedData = JSON.parse(stored);
                    } catch (e) {}

                    if (processedData.date === todayStr && processedData.slots[timeStr]) continue;

                    if (isExecutingRef.current) return;
                    console.log(`[V40 Scheduler] 🎯 Triggering check for ${timeStr} slot...`);
                    isExecutingRef.current = true;

                    const history = await apiService.getLivermoreV40History();
                    const alreadyExists = history.some(h => {
                        const hDate = new Date(h.createdAt);
                        const hTimeMins = hDate.getHours() * 60 + hDate.getMinutes();
                        const hKstDate = new Date(hDate.getTime() + 9 * 60 * 60 * 1000);
                        const hDateStr = hKstDate.toISOString().split('T')[0];
                        return hDateStr === todayStr && Math.abs(hTimeMins - slotMins) < 15;
                    });

                    if (!alreadyExists) {
                        console.log(`[V40 Scheduler] Saving snapshot for ${timeStr}...`);
                        await apiService.saveLivermoreV40Snapshot();
                        await load();
                    }

                    processedData.date = todayStr;
                    processedData.slots[timeStr] = true;
                    localStorage.setItem(storageKey, JSON.stringify(processedData));
                    isExecutingRef.current = false;
                }
            }
        } catch (e) {
            console.error('[V40 Scheduler] Error:', e);
            isExecutingRef.current = false;
        }
    }, [load]);

    useEffect(() => {
        if (!schedulerConfig || !schedulerConfig.enabled) return;
        const intervalId = setInterval(() => {
            runAutoSaveProcedure(schedulerConfig);
        }, 10000);
        return () => clearInterval(intervalId);
    }, [schedulerConfig, runAutoSaveProcedure]);

    const handleUpdateScheduler = async (updates) => {
        try {
            const res = await apiService.updateLivermoreV40Scheduler({ ...schedulerConfig, ...updates });
            if (res.success) {
                setSchedulerConfig(res.status.config);
                runAutoSaveProcedure(res.status.config);
            }
        } catch (err) {
            alert(`스케줄러 업데이트 실패: ${err.message}`);
        }
    };

    const saveSnapshot = async () => {
        try {
            setLoading(true);
            await apiService.saveLivermoreV40Snapshot();
            await load(true);
        } catch (e) {
            setError(e.message);
            setLoading(false);
        }
    };

    const loadRawCandidates = async () => {
        setRawLoading(true);
        setRawError(null);
        setRawCandidates(null);
        setShowRaw(false);
        try {
            const res = await apiService.getLivermoreV40RawCandidates();
            if (res.error || !res.items || res.items.length === 0) {
                setRawError(res.error || 'KIS API에서 종목을 가져오지 못했습니다. (0개 반환)');
            } else {
                setRawCandidates(res.items);
                setShowRaw(true);
            }
        } catch (e) {
            setRawError(`KIS API 호출 실패: ${e.message}`);
        } finally {
            setRawLoading(false);
        }
    };

    const runApiCheck = async () => {
        setApiCheckLoading(true);
        setApiCheckResult(null);
        setShowApiCheck(true);
        try {
            const res = await apiService.checkLivermoreV40ApiHealth();
            setApiCheckResult(res);
        } catch (e) {
            setApiCheckResult({ error: e.message });
        } finally {
            setApiCheckLoading(false);
        }
    };

    const sortedRanked = useMemo(() => {
        if (!data?.ranked) return [];
        const arr = [...data.ranked];
        if (sortKey === 'total') arr.sort((a, b) => b.totalScore - a.totalScore);
        else if (sortKey === 'pivot') arr.sort((a, b) => b.pivotScore - a.pivotScore);
        else if (sortKey === 'leader') arr.sort((a, b) => (b.leaderScore || 0) - (a.leaderScore || 0));
        else if (sortKey === 'thresholds') arr.sort((a, b) => (b.thresholdsPassed || 0) - (a.thresholdsPassed || 0));

        // 점수 계산에서 누락된 후보들 하단에 추가
        const rankedCodes = new Set(arr.map(r => r.code));
        const unscored = (data.leaders || [])
            .filter(l => !rankedCodes.has(l.code))
            .map(l => ({ ...l, isUnscored: true, leaderScore: l.totalScore }));

        return [...arr, ...unscored];
    }, [data, sortKey]);

    const toggle = (code) => setExpanded(prev => ({ ...prev, [code]: !prev[code] }));

    return (
        <PageWrap>
            <Header>
                <Title>
                    <Trophy size={22} color="#a855f7" />
                    리버모어 스코어링
                    <span className="badge">V4.0</span>
                </Title>
                <Controls>
                    {marketClosed && (
                        <MarketClosedBadge>
                            <AlertCircle size={14} />MARKET CLOSED
                        </MarketClosedBadge>
                    )}
                    <Btn onClick={runApiCheck} disabled={apiCheckLoading} style={apiCheckLoading ? { opacity: 0.6, cursor: 'default' } : {}}>
                        {apiCheckLoading
                            ? <SpinIcon><Loader size={14} /></SpinIcon>
                            : <Stethoscope size={14} />}
                        {apiCheckLoading ? '점검 중...' : 'API 점검'}
                    </Btn>
                    <Btn onClick={loadRawCandidates} disabled={rawLoading} style={rawLoading ? { opacity: 0.6, cursor: 'default' } : {}}>
                        <Database size={14} />{rawLoading ? '조회 중...' : 'KIS 원본'}
                    </Btn>
                    {data?.leaders?.length > 0 && (
                        <Btn onClick={() => setShowCandidates(true)}>
                            <List size={14} />후보종목 ({data.leaders.length})
                        </Btn>
                    )}
                    <Btn onClick={() => setShowSettings(true)}>
                        <Settings size={14} />설정
                        {schedulerConfig?.enabled && (
                            <span style={{
                                width: 6, height: 6, background: '#a855f7', borderRadius: '50%', marginLeft: 4,
                                boxShadow: '0 0 8px #a855f7'
                            }} />
                        )}
                    </Btn>
                    <Btn onClick={() => navigate('/labs/livermore-v40/history')}>
                        <History size={14} />히스토리
                    </Btn>
                    <Btn onClick={() => load(true)}>
                        <RefreshCw size={14} />새로고침
                    </Btn>
                    <Btn primary onClick={saveSnapshot}>
                        <Zap size={14} />스냅샷 저장
                    </Btn>
                </Controls>
            </Header>

            <StatusBar>
                <LiveDot />
                <span style={{ color: '#94a3b8' }}>V4.0 점수 모델:</span>
                <InfoTag>가중치 Leader 0.5 + Pivot 0.5</InfoTag>
                <InfoTag>7개 항목 100점 만점</InfoTag>
                <InfoTag>탈락 없음 (전체 랭킹)</InfoTag>
                {data?.leaders && <InfoTag>후보군 {data.leaders.length}개</InfoTag>}
                {data?.ranked && <InfoTag>점수계산 {data.ranked.length}개</InfoTag>}
                {data?.leaders && data?.ranked && (() => {
                    const cnt = (data.leaders || []).filter(l => !data.ranked.find(r => r.code === l.code)).length;
                    return cnt > 0 ? <InfoTag style={{ background: 'rgba(100,116,139,.12)', color: '#94a3b8', borderColor: 'rgba(100,116,139,.2)' }}>미계산 {cnt}개</InfoTag> : null;
                })()}
                {lastUpdate && <span style={{ marginLeft: 'auto', color: '#475569', fontSize: '12px' }}>
                    <Clock size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    {lastUpdate.toLocaleTimeString('ko-KR')}
                </span>}
            </StatusBar>

            {showApiCheck && (
                <ModalOverlay onClick={() => setShowApiCheck(false)}>
                    <ApiCheckModal onClick={e => e.stopPropagation()}>
                        {/* 헤더 */}
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'20px 24px', borderBottom:'1px solid rgba(255,255,255,.08)' }}>
                            <h2 style={{ margin:0, fontSize:16, display:'flex', alignItems:'center', gap:8 }}>
                                <Stethoscope size={16} color="#a855f7" />
                                신규 API 동작 점검
                            </h2>
                            <button onClick={() => setShowApiCheck(false)} style={{ background:'none', border:'none', color:'#64748b', cursor:'pointer' }}><X size={20} /></button>
                        </div>

                        {apiCheckLoading && (
                            <div style={{ padding:'40px', textAlign:'center', color:'#a855f7', display:'flex', flexDirection:'column', alignItems:'center', gap:12 }}>
                                <SpinIcon><Loader size={28} /></SpinIcon>
                                <div style={{ fontSize:13, color:'#64748b' }}>KIS API 호출 중... (대량체결건수 + 삼성전자 프로그램매매)</div>
                            </div>
                        )}

                        {!apiCheckLoading && apiCheckResult?.error && (
                            <div style={{ padding:'24px' }}>
                                <ApiStatusBadge $error><XCircle size={13} /> 요청 실패</ApiStatusBadge>
                                <div style={{ marginTop:12, fontSize:12, color:'#f87171', fontFamily:'monospace' }}>{apiCheckResult.error}</div>
                            </div>
                        )}

                        {!apiCheckLoading && apiCheckResult && !apiCheckResult.error && (() => {
                            const { bulkTransNum, programBuy, checkedAt } = apiCheckResult;
                            return (
                                <>
                                    {/* 대량체결건수상위 */}
                                    <ApiCheckSection>
                                        <ApiStatusRow>
                                            {bulkTransNum.ok
                                                ? <ApiStatusBadge $ok><CheckCircle size={13} /> 성공</ApiStatusBadge>
                                                : <ApiStatusBadge $error><XCircle size={13} /> 실패</ApiStatusBadge>}
                                            <span style={{ fontSize:14, fontWeight:600 }}>대량체결건수상위</span>
                                            <span style={{ fontSize:11, color:'#475569', marginLeft:'auto' }}>TR: {bulkTransNum.trId || 'FHPST01720000'}</span>
                                        </ApiStatusRow>
                                        <div style={{ fontSize:11, color:'#475569', marginBottom:8 }}>
                                            FID_COND_SCR_DIV_CODE: {bulkTransNum.scrDivCode || '20174'} · FID_COND_MRKT_DIV_CODE: J
                                        </div>

                                        {/* KIS 응답 코드 표시 (성공/실패 모두) */}
                                        {(bulkTransNum.rtCd || bulkTransNum.msgCd || bulkTransNum.msg1) && (
                                            <FieldTable style={{ marginBottom:10 }}>
                                                <div style={{ color:'#475569', marginBottom:4, fontSize:10 }}>KIS 응답 코드</div>
                                                {bulkTransNum.status && <FieldRow><span className="key">HTTP status</span><span className="val">{bulkTransNum.status}</span></FieldRow>}
                                                {bulkTransNum.rtCd  && <FieldRow><span className="key">rt_cd</span><span className={bulkTransNum.rtCd === '0' ? 'val' : 'zero' + '" style="color:#f87171'}>{bulkTransNum.rtCd}</span></FieldRow>}
                                                {bulkTransNum.msgCd && <FieldRow><span className="key">msg_cd</span><span className="val">{bulkTransNum.msgCd}</span></FieldRow>}
                                                {bulkTransNum.msg1  && <FieldRow><span className="key">msg1</span><span className="val">{bulkTransNum.msg1}</span></FieldRow>}
                                            </FieldTable>
                                        )}

                                        {bulkTransNum.error && (
                                            <div style={{ fontSize:12, color:'#f87171', fontFamily:'monospace', marginBottom:10, wordBreak:'break-all' }}>{bulkTransNum.error}</div>
                                        )}

                                        {bulkTransNum.ok && (
                                            <>
                                                <div style={{ fontSize:12, color:'#94a3b8', marginBottom:8 }}>
                                                    조회 종목 수: <strong style={{ color:'#d8b4fe' }}>{bulkTransNum.count}개</strong>
                                                </div>
                                                <FieldTable>
                                                    <div style={{ color:'#475569', marginBottom:6, fontSize:10 }}>상위 5종목</div>
                                                    {bulkTransNum.sample.map(s => (
                                                        <FieldRow key={s.code}>
                                                            <span className="key">#{s.rank} {s.code}</span>
                                                            <span className="val">{s.name}</span>
                                                        </FieldRow>
                                                    ))}
                                                </FieldTable>
                                                {bulkTransNum.rawFirstItem && (
                                                    <FieldTable style={{ marginTop:8 }}>
                                                        <div style={{ color:'#475569', marginBottom:6, fontSize:10 }}>1위 종목 원본 필드</div>
                                                        {Object.entries(bulkTransNum.rawFirstItem).map(([k, v]) => (
                                                            <FieldRow key={k}>
                                                                <span className="key">{k}</span>
                                                                <span className={(!v || v === '0' || v === '') ? 'zero' : 'val'}>{v === '' ? '(empty)' : String(v)}</span>
                                                            </FieldRow>
                                                        ))}
                                                    </FieldTable>
                                                )}
                                            </>
                                        )}
                                    </ApiCheckSection>

                                    {/* 프로그램매매 비차익매수 */}
                                    <ApiCheckSection>
                                        <ApiStatusRow>
                                            {programBuy.ok
                                                ? <ApiStatusBadge $ok><CheckCircle size={13} /> 성공</ApiStatusBadge>
                                                : <ApiStatusBadge $error><XCircle size={13} /> 실패</ApiStatusBadge>}
                                            <span style={{ fontSize:14, fontWeight:600 }}>프로그램매매 비차익매수</span>
                                            <span style={{ fontSize:11, color:'#475569', marginLeft:'auto' }}>TR: FHPPG04650201</span>
                                        </ApiStatusRow>
                                        <div style={{ fontSize:11, color:'#475569', marginBottom:10 }}>테스트 종목: 삼성전자 (005930)</div>
                                        {programBuy.error && (
                                            <div style={{ fontSize:12, color:'#f87171', fontFamily:'monospace', marginBottom:10 }}>{programBuy.error}</div>
                                        )}
                                        {programBuy.ok && (
                                            <>
                                                <div style={{ fontSize:12, color:'#94a3b8', marginBottom:8 }}>
                                                    비차익 순매수:&nbsp;
                                                    <strong style={{ color: programBuy.netBuyAmt > 0 ? '#4ade80' : programBuy.netBuyAmt < 0 ? '#f87171' : '#94a3b8' }}>
                                                        {fmtAmt(programBuy.netBuyAmt)}
                                                    </strong>
                                                </div>
                                                {programBuy.rawFields && (
                                                    <FieldTable>
                                                        <div style={{ color:'#475569', marginBottom:6, fontSize:10 }}>원본 응답 필드 (output[0])</div>
                                                        {Object.entries(programBuy.rawFields).map(([k, v]) => (
                                                            <FieldRow key={k}>
                                                                <span className="key">{k}</span>
                                                                <span className={(!v || v === '0' || v === '') ? 'zero' : 'val'}>{v === '' ? '(empty)' : String(v)}</span>
                                                            </FieldRow>
                                                        ))}
                                                    </FieldTable>
                                                )}
                                            </>
                                        )}
                                    </ApiCheckSection>

                                    <div style={{ padding:'12px 24px', fontSize:11, color:'#334155', textAlign:'right' }}>
                                        점검 시각: {checkedAt ? new Date(checkedAt).toLocaleString('ko-KR') : '-'}
                                    </div>
                                </>
                            );
                        })()}
                    </ApiCheckModal>
                </ModalOverlay>
            )}

            {showSettings && schedulerConfig && (
                <ModalOverlay onClick={() => setShowSettings(false)}>
                    <Modal onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                            <h2 style={{ fontSize: '18px', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Clock size={18} color="#a855f7" /> 스케줄러 설정
                            </h2>
                            <button onClick={() => setShowSettings(false)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><X size={20} /></button>
                        </div>

                        <div style={{ marginBottom: 24 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '14px' }}>
                                <input
                                    type="checkbox"
                                    checked={schedulerConfig.enabled}
                                    onChange={e => handleUpdateScheduler({ enabled: e.target.checked })}
                                />
                                자동 분석 및 스냅샷 저장 켜기
                            </label>
                        </div>

                        <div style={{ marginBottom: 12, fontSize: '12px', color: '#64748b', textTransform: 'uppercase' }}>실행 시간 목록</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {schedulerConfig.times.map(time => (
                                <div key={time} style={{ display: 'flex', justifyContent: 'space-between', background: 'rgba(255,255,255,.03)', padding: '10px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,.05)' }}>
                                    <span>{time}</span>
                                    <button
                                        onClick={() => handleUpdateScheduler({ times: schedulerConfig.times.filter(t => t !== time) })}
                                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
                            <input
                                type="time"
                                value={newTime}
                                onChange={e => setNewTime(e.target.value)}
                                style={{ flex: 1, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)', color: '#fff', padding: '10px', borderRadius: 8 }}
                            />
                            <button
                                onClick={() => {
                                    if (!schedulerConfig.times.includes(newTime)) {
                                        handleUpdateScheduler({ times: [...schedulerConfig.times, newTime].sort() });
                                    }
                                }}
                                style={{ background: '#a855f7', color: '#0a0d12', border: 'none', padding: '10px 16px', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
                            >
                                <Plus size={16} />
                            </button>
                        </div>
                    </Modal>
                </ModalOverlay>
            )}

            {showCandidates && data?.leaders && (
                <ModalOverlay onClick={() => setShowCandidates(false)}>
                    <CandidatesModal onClick={e => e.stopPropagation()}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'20px 20px 14px', borderBottom:'1px solid rgba(255,255,255,.08)', flexShrink:0 }}>
                            <h2 style={{ fontSize:'16px', margin:0, display:'flex', alignItems:'center', gap:8 }}>
                                <List size={16} color="#a855f7" />
                                후보종목 리스트 <span style={{ fontSize:'12px', color:'#64748b', fontWeight:400 }}>V3.1 리더 풀 ({data.leaders.length}개)</span>
                            </h2>
                            <button onClick={() => setShowCandidates(false)} style={{ background:'none', border:'none', color:'#64748b', cursor:'pointer' }}><X size={20} /></button>
                        </div>
                        <CandidatesBody>
                            <div style={{ overflowX:'auto' }}>
                                <CandHead>
                                    <div>#</div>
                                    <div>종목명</div>
                                    <div style={{ textAlign:'right' }}>현재가</div>
                                    <div style={{ textAlign:'right' }}>거래대금</div>
                                    <div style={{ textAlign:'right' }}>시총</div>
                                    <div style={{ textAlign:'right' }}>52주근접</div>
                                    <div style={{ textAlign:'center' }}>V31점수</div>
                                </CandHead>
                                {data.leaders.map((l, i) => {
                                    const inRanked = data.ranked?.some(r => r.code === l.code);
                                    return (
                                        <CandRow key={l.code}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => window.open(`/labs/stock/technical?symbol=${l.code}`, '_blank')}
                                        >
                                            <div style={{ color:'#64748b', fontWeight:700 }}>{i + 1}</div>
                                            <div style={{ display:'flex', flexDirection:'column', gap:2, minWidth:0 }}>
                                                <span style={{ color:'#e2e8f0', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.name}</span>
                                                <span style={{ fontSize:'10px', color: inRanked ? '#a855f7' : '#475569' }}>
                                                    {inRanked ? '✓ V4.0 포함' : '미계산'}
                                                </span>
                                            </div>
                                            <div style={{ textAlign:'right' }}>
                                                <div style={{ color:'#e2e8f0' }}>{l.price?.toLocaleString()}원</div>
                                                <div style={{ fontSize:'10px', color: l.changeRate >= 0 ? '#ef4444' : '#3b82f6' }}>
                                                    {l.changeRate >= 0 ? '+' : ''}{l.changeRate?.toFixed(2)}%
                                                </div>
                                            </div>
                                            <div style={{ textAlign:'right', color:'#94a3b8' }}>{l.trValueFormatted || '-'}</div>
                                            <div style={{ textAlign:'right', color:'#94a3b8' }}>{l.mcapFormatted || '-'}</div>
                                            <div style={{ textAlign:'right', color:'#94a3b8' }}>{l.highProximity || '-'}</div>
                                            <div style={{ textAlign:'center' }}>
                                                <GradeBadge $g={l.grade}>{l.grade}</GradeBadge>
                                                <div style={{ fontSize:'10px', color:'#64748b', marginTop:2 }}>{l.totalScore?.toFixed(0)}점</div>
                                            </div>
                                        </CandRow>
                                    );
                                })}
                            </div>
                        </CandidatesBody>
                    </CandidatesModal>
                </ModalOverlay>
            )}

            {showRaw && rawCandidates && (
                <ModalOverlay onClick={() => setShowRaw(false)}>
                    <RawCandModal onClick={e => e.stopPropagation()}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'20px 20px 14px', borderBottom:'1px solid rgba(255,255,255,.08)', flexShrink:0 }}>
                            <h2 style={{ fontSize:'16px', margin:0, display:'flex', alignItems:'center', gap:8 }}>
                                <Database size={16} color="#a855f7" />
                                KIS 원시 후보 목록
                                <span style={{ fontSize:'12px', color:'#64748b', fontWeight:400 }}>
                                    총 {rawCandidates.length}개 · 통과 {rawCandidates.filter(r => !r.filterReason).length}개 · 필터 {rawCandidates.filter(r => r.filterReason).length}개
                                </span>
                            </h2>
                            <button onClick={() => setShowRaw(false)} style={{ background:'none', border:'none', color:'#64748b', cursor:'pointer' }}><X size={20} /></button>
                        </div>
                        <CandidatesBody>
                            <div style={{ overflowX:'auto' }}>
                                <RawCandHead>
                                    <div>#</div>
                                    <div>종목명</div>
                                    <div style={{ textAlign:'right' }}>현재가</div>
                                    <div style={{ textAlign:'right' }}>거래대금</div>
                                    <div style={{ textAlign:'right' }}>시총</div>
                                    <div>필터 결과</div>
                                </RawCandHead>
                                {rawCandidates.map((s, i) => (
                                    <RawCandRow key={s.code} $filtered={!!s.filterReason}
                                        onClick={() => !s.filterReason && window.open(`/labs/stock/technical?symbol=${s.code}`, '_blank')}
                                        style={{ cursor: s.filterReason ? 'default' : 'pointer' }}
                                    >
                                        <div style={{ color:'#64748b', fontWeight:700 }}>{i + 1}</div>
                                        <div style={{ display:'flex', flexDirection:'column', gap:1, minWidth:0 }}>
                                            <span style={{ color: s.filterReason ? '#64748b' : '#e2e8f0', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.name}</span>
                                            <span style={{ display:'flex', gap:4, alignItems:'center' }}>
                                                <span style={{ fontSize:'10px', color:'#475569' }}>{s.code}</span>
                                                {s.src === 'naver' && (
                                                    <span style={{ fontSize:'9px', padding:'1px 4px', borderRadius:3, background:'rgba(34,197,94,.1)', color:'#4ade80', border:'1px solid rgba(34,197,94,.2)', fontWeight:700 }}>Naver</span>
                                                )}
                                            </span>
                                        </div>
                                        <div style={{ textAlign:'right' }}>
                                            <div style={{ color: s.filterReason ? '#64748b' : '#e2e8f0' }}>{s.price?.toLocaleString()}원</div>
                                            <div style={{ fontSize:'10px', color: s.changeRate >= 0 ? '#ef4444' : '#3b82f6' }}>
                                                {s.changeRate >= 0 ? '+' : ''}{s.changeRate?.toFixed(2)}%
                                            </div>
                                        </div>
                                        <div style={{ textAlign:'right', color:'#94a3b8' }}>{s.trValueFormatted}</div>
                                        <div style={{ textAlign:'right', color:'#94a3b8' }}>{s.mcapFormatted}</div>
                                        <div>
                                            {s.filterReason
                                                ? <FilterTag>{s.filterReason}</FilterTag>
                                                : <PassTag>통과</PassTag>
                                            }
                                        </div>
                                    </RawCandRow>
                                ))}
                            </div>
                        </CandidatesBody>
                    </RawCandModal>
                </ModalOverlay>
            )}

            {rawLoading && (
                <RawLoadingBar>
                    <Database size={15} />
                    KIS API에서 원시 후보 목록을 가져오는 중입니다. 잠시 기다려 주세요...
                    <span style={{ marginLeft:'auto', fontSize:'12px', color:'#64748b' }}>시총·52주고가 조회 포함 (30~60초 소요)</span>
                </RawLoadingBar>
            )}

            {rawError && (
                <InlineError>
                    <AlertCircle size={18} color="#ef4444" style={{ flexShrink:0, marginTop:1 }} />
                    <div>
                        <div style={{ color:'#ef4444', fontWeight:600, marginBottom:4 }}>KIS API 오류</div>
                        <div style={{ color:'#f87171', fontSize:'13px' }}>{rawError}</div>
                        <button onClick={loadRawCandidates} style={{ marginTop:10, background:'rgba(239,68,68,.12)', border:'1px solid rgba(239,68,68,.25)', color:'#f87171', padding:'6px 14px', borderRadius:6, cursor:'pointer', fontSize:'12px', fontWeight:600 }}>
                            다시 시도
                        </button>
                    </div>
                </InlineError>
            )}

            {error && (
                <Card style={{ borderColor: 'rgba(239,68,68,.2)', marginBottom: 20, padding: 16 }}>
                    <span style={{ color: '#ef4444' }}>⚠️ {error}</span>
                </Card>
            )}

            <SortBar>
                <ArrowUpDown size={13} /> 정렬:
                <SortBtn active={sortKey === 'total'} onClick={() => setSortKey('total')}>총점</SortBtn>
                <SortBtn active={sortKey === 'pivot'} onClick={() => setSortKey('pivot')}>Pivot 점수</SortBtn>
                <SortBtn active={sortKey === 'leader'} onClick={() => setSortKey('leader')}>Leader 점수</SortBtn>
                <SortBtn active={sortKey === 'thresholds'} onClick={() => setSortKey('thresholds')}>기준치 통과수</SortBtn>
            </SortBar>

            {loading ? (
                <LoadingDot>◉ 분석 중...</LoadingDot>
            ) : !data?.ranked || data.ranked.length === 0 ? (
                <Card><Empty>분석된 종목이 없습니다</Empty></Card>
            ) : (
                <Card>
                    <TableWrapper>
                        <TableHead>
                            <div style={{ textAlign: 'center' }}>#</div>
                            <div>종목</div>
                            <div style={{ textAlign: 'right' }}>총점</div>
                            <div style={{ textAlign: 'right' }}>Leader</div>
                            <div style={{ textAlign: 'right' }}>Pivot</div>
                            <div style={{ textAlign: 'center' }}>통과</div>
                            <div style={{ textAlign: 'center' }}>V31 판정</div>
                            <div></div>
                        </TableHead>

                        {sortedRanked.map((s, idx) => {
                            const isFirstUnscored = s.isUnscored && (idx === 0 || !sortedRanked[idx - 1].isUnscored);
                            const isOpen = !!expanded[s.code];
                            const passRatio = (s.thresholdsPassed || 0) / (s.thresholdsTotal || 7);
                            return (
                                <React.Fragment key={s.code}>
                                    {isFirstUnscored && (
                                        <UnscoredDivider>── 아래는 점수 미계산 후보 (데이터 부족 등) ──</UnscoredDivider>
                                    )}
                                    <Row $unscored={s.isUnscored} onClick={() => !s.isUnscored && toggle(s.code)}>
                                        <RankBadge $rank={s.isUnscored ? 999 : s.rank}>
                                            {s.isUnscored ? '–' : s.rank}
                                        </RankBadge>
                                        <NameCell>
                                            <div className="name">{s.name}</div>
                                            <div className="meta">
                                                <span>{s.price?.toLocaleString()}원</span>
                                                <span style={{ color: s.changeRate >= 0 ? '#ef4444' : '#3b82f6' }}>
                                                    {s.changeRate >= 0 ? '+' : ''}{s.changeRate?.toFixed(2)}%
                                                </span>
                                                {s.isTrendStarter && (
                                                    <span style={{ background: 'linear-gradient(135deg,#10b981,#059669)', color: '#fff', padding: '1px 5px', borderRadius: '3px', fontSize: '9px', fontWeight: 700 }}>
                                                        👑추세초입
                                                    </span>
                                                )}
                                            </div>
                                        </NameCell>
                                        <ScoreNum $big $score={s.isUnscored ? 0 : s.totalScore}>
                                            {s.isUnscored ? '–' : s.totalScore?.toFixed(1)}
                                        </ScoreNum>
                                        <SubScore>{s.leaderScore?.toFixed(1) ?? '–'}</SubScore>
                                        <SubScore>{s.isUnscored ? '–' : s.pivotScore?.toFixed(1)}</SubScore>
                                        <div style={{ textAlign: 'center' }}>
                                            {s.isUnscored ? (
                                                <span style={{ color: '#475569', fontSize: '11px' }}>–</span>
                                            ) : (
                                                <PassPill
                                                    $full={passRatio >= 1}
                                                    $high={passRatio >= 0.7 && passRatio < 1}
                                                    $mid={passRatio >= 0.4 && passRatio < 0.7}
                                                >
                                                    {s.thresholdsPassed}/{s.thresholdsTotal}
                                                </PassPill>
                                            )}
                                        </div>
                                        <div style={{ textAlign: 'center' }}>
                                            {s.isUnscored ? (
                                                <span style={{ color: '#475569', fontSize: '10px' }}>미계산</span>
                                            ) : (
                                                <VerdictBadge $v={s.v31Verdict}>{s.v31Verdict}</VerdictBadge>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center', color: '#64748b' }}>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/labs/stock/technical?symbol=${s.code}${s.resistanceLine ? `&pivot=${s.resistanceLine}` : ''}`);
                                                }}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    color: '#a855f7',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: 4
                                                }}
                                                title="차트 보기"
                                            >
                                                <TrendingUp size={14} />
                                            </button>
                                            {!s.isUnscored && (isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                                        </div>
                                    </Row>

                                {isOpen && !s.isUnscored && s.components && (
                                    <Detail>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 12, color: '#64748b' }}>
                                            <span>항목별 점수 분해 (Pivot {s.pivotScore?.toFixed(1)} / 100)</span>
                                            <span
                                                style={{ color: '#a855f7', cursor: 'pointer', fontWeight: 600 }}
                                                onClick={(e) => { e.stopPropagation(); window.open(`/labs/stock/technical?symbol=${s.code}${s.resistanceLine ? `&pivot=${s.resistanceLine}` : ''}`, '_blank'); }}
                                            >
                                                차트 보기 →
                                            </span>
                                        </div>
                                        {Object.entries(s.components).map(([key, c]) => {
                                            const pct = (c.score / c.max) * 100;
                                            return (
                                                <ComponentRow key={key}>
                                                    <div style={{ color: c.pass ? '#d8b4fe' : '#64748b', fontWeight: 600 }}>
                                                        {c.pass ? '✅' : '❌'} {COMPONENT_LABELS[key]}
                                                    </div>
                                                    <Bar><BarFill pct={pct} pass={c.pass} /></Bar>
                                                    <div style={{ textAlign: 'right', color: '#94a3b8', fontWeight: 600 }}>
                                                        {c.score.toFixed(1)} / {c.max}
                                                    </div>
                                                    <div style={{ textAlign: 'right', color: '#475569', fontSize: 11 }}>
                                                        {formatComponentValue(key, c)}
                                                    </div>
                                                </ComponentRow>
                                            );
                                        })}
                                    </Detail>
                                )}
                            </React.Fragment>
                        );
                    })}
                    </TableWrapper>
                </Card>
            )}
        </PageWrap>
    );
}
